import { LoginPhase } from 'navigation/models/login-phase';
import { HOME_PATH } from 'navigation/routes';
import { createContext, Reducer, useCallback, useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'shared/hooks/useLocalStorage';
import { LOGIN_PATH, PASSWORD_EXPIRED_PATH, SYSTEM_ACCESS_PATH } from 'user/routes';
import { AuthProvider } from './AuthProvider';
import { LoginData } from 'auth/models';
import { refreshAuthentication } from 'auth/services/useAuthService';

interface ISecurityContext {
  accessToken: string;
  refreshToken: string;
  loginPhase: LoginPhase;
  saveTokens(tokens: LoginData): any;
  removeTokens(): void;
  clearTokens(): void;
  authenticate(): Promise<LoginData>;
  saveLoginPhase: (loginPhase: LoginPhase) => void;
}

interface ContextReducerState {
  accessToken: string;
  refreshToken: string;
  loginPhase: LoginPhase;
}

export const SecurityContext = createContext<ISecurityContext>({
  accessToken: null,
  refreshToken: null,
  loginPhase: null,
  saveTokens: () => {},
  removeTokens: () => {},
  clearTokens: () => {},
  authenticate: () => null,
  saveLoginPhase: () => {},
});

const getRoute = (loginPhase: LoginPhase): string => {
  switch (loginPhase) {
    case LoginPhase.EXPIRED_PASSWORD:
      return PASSWORD_EXPIRED_PATH;
    case LoginPhase.LOGGED_IN:
      return HOME_PATH;
    case LoginPhase.LOGIN:
      return LOGIN_PATH;
    case LoginPhase.EMAIL_REQUIRED_BLOCKED:
      return SYSTEM_ACCESS_PATH;
    default:
      return LOGIN_PATH;
  }
};

const allowedRoutes: { [key: string]: RegExp } = {
  login: /^\/(adm\/)?login$/,
  expired_password: /^\/(password\/expired)$/,
  register_email: /^\/(email\/register)$/,
  logged_in: /^\/(?!login).*$/,
  email_required_blocked: /^\/system-access$/,
};

interface ContextReducerAction {
  type: 'CLEAR' | 'SAVE_TOKEN' | 'SAVE_PHASE';
  tokens?: LoginData;
  loginPhase?: LoginPhase;
}

const LOCALSTORAGE_KEY = 'sec';

const PUBLIC_ROUTES = [
  /^\/(password\/recover)$/,
  /^\/(password\/reset\/.+)$/,
  /^\/(email\/confirmation\/.+)$/,
];

const routeAllowedByLoginPhase = (loginPhase: LoginPhase, route: string): boolean => {
  // todas las fases permiten rutas publicas
  if (isPublicRoute(route)) {
    return true;
  }
  if (!loginPhase && isLoginRoute(route)) {
    return true;
  }
  if (!loginPhase) {
    return false;
  }

  return allowedRoutes[loginPhase].test(route || '');
};

const isLoginRoute = (route: string) => {
  return route === LOGIN_PATH;
};

const isPublicRoute = (route: string) => {
  let isPublic = false;

  for (const expression of PUBLIC_ROUTES) {
    const regex = new RegExp(expression);

    if (regex.test(route)) {
      isPublic = true;
      break;
    }
  }

  return isPublic;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'CLEAR':
      return {
        ...state,
        accessToken: null,
        refreshToken: null,
        loginPhase: null,
      };
    case 'SAVE_TOKEN':
      return {
        ...state,
        accessToken: action.tokens.accessToken,
        refreshToken: action.tokens.refreshToken,
      };
    case 'SAVE_PHASE':
      return {
        ...state,
        loginPhase: action.loginPhase,
      };
  }
};

export const SecurityProvider = ({ children }) => {
  const { getItem, saveItem, deleteItem } = useLocalStorage();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [{ accessToken, refreshToken, loginPhase }, dispatch] = useReducer<
    Reducer<ContextReducerState, ContextReducerAction>
  >(reducer, getItem(LOCALSTORAGE_KEY) ?? {});

  useEffect(() => {
    if (accessToken || loginPhase) {
      saveItem(LOCALSTORAGE_KEY, { accessToken, refreshToken, loginPhase });
    } else {
      deleteItem(LOCALSTORAGE_KEY);
    }
  }, [accessToken, refreshToken, loginPhase, saveItem, deleteItem]);

  useEffect(() => {
    let route: string;

    if (!loginPhase) {
      // si no hay una fase de login activa redirigimos al login correspondiente
      route = LOGIN_PATH;
    } else if (!routeAllowedByLoginPhase(loginPhase, pathname)) {
      // si se detecta un cambio en la fase de login se hace la redireccion
      route = getRoute(loginPhase);
    }

    // hacemos la redireccion solamente si la ruta actual (pathname) a la que queremos acceder no es publica
    if (!!route && !isPublicRoute(pathname)) {
      navigate(route);
    }
  }, [navigate, loginPhase, pathname]);

  const contextValue = {
    accessToken,
    refreshToken,
    loginPhase,
    saveTokens(tokens: LoginData) {
      dispatch({
        type: 'SAVE_TOKEN',
        tokens,
      });
    },
    removeTokens: useCallback(() => {
      dispatch({
        type: 'CLEAR',
      });
    }, []),
    clearTokens: useCallback(() => {
      dispatch({
        type: 'CLEAR',
      });
    }, []),
    saveLoginPhase: useCallback((loginPhase: LoginPhase) => {
      dispatch({
        type: 'SAVE_PHASE',
        loginPhase,
      });
    }, []),
    authenticate: useCallback(async () => {
      // Intentamos refrescar los tokens
      const tokens = await refreshAuthentication(accessToken, refreshToken);

      if (tokens) {
        // Si la respuesta esta ok actualizamos los tokens
        dispatch({
          type: 'SAVE_TOKEN',
          tokens,
        });
      } else {
        // Si hubo un error con el request, entonces borramos los tokens y obligamos al usuario a loguearse de nuevo
        dispatch({
          type: 'CLEAR',
        });
      }
      return tokens;
    }, [accessToken, refreshToken]),
  };
  return (
    <SecurityContext.Provider value={contextValue}>
      <AuthProvider>
        {routeAllowedByLoginPhase(loginPhase, pathname) && children}
      </AuthProvider>
    </SecurityContext.Provider>
  );
};
